import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, InputAdornment, LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow, TextField, Typography, alpha, useTheme,
} from '@mui/material';
import { MenuBookOutlined, Search } from '@mui/icons-material';
import financeService from '../../../services/financeService';
import { JOURNAL_SOURCE_LABEL, JOURNAL_SOURCE_OPTIONS, JournalListResponse, JournalStatus } from '../../../types/finance';
import { useTimezone } from '../../../hooks/useTimezone';
import { useDebounce } from '../../../hooks/useDebounce';
import DateRangePicker, { DateRange } from '../../common/DateRangePicker';
import SearchableSelect, { SelectOption } from '../../common/SearchableSelect';
import TableRowSkeletons from '../../common/TableRowSkeletons';
import EmptyState from '../../common/EmptyState';
import Amount from '../ledger/Amount';
import JournalStatusChip from './JournalStatusChip';

const STATUS_OPTIONS = [
  { value: 'POSTED', label: 'Posted' },
  { value: 'REVERSED', label: 'Reversed' },
];

/** Every voucher, newest first — filtered and paged on the server. */
const JournalEntriesTable: React.FC<{
  accountOptions: SelectOption[];
  refreshKey: number;
  onOpen: (entryId: string) => void;
}> = ({ accountOptions, refreshKey, onOpen }) => {
  const theme = useTheme();
  const { formatDate } = useTimezone();
  const [q, setQ] = useState('');
  const [range, setRange] = useState<DateRange>({ from: null, to: null });
  const [accountId, setAccountId] = useState('');
  const [sourceType, setSourceType] = useState('');
  const [status, setStatus] = useState<JournalStatus | ''>('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [data, setData] = useState<JournalListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const search = useDebounce(q, 300);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await financeService.listJournal({
        q: search, from: range.from, to: range.to, accountId, sourceType, status, page: page + 1, limit,
      }));
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Could not load the journal.');
    } finally {
      setLoading(false);
    }
  }, [search, range, accountId, sourceType, status, page, limit]);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { setPage(0); }, [search, range, accountId, sourceType, status]);

  const items = data?.items ?? [];
  const filtered = !!(q || range.from || range.to || accountId || sourceType || status);
  const reset = () => { setQ(''); setRange({ from: null, to: null }); setAccountId(''); setSourceType(''); setStatus(''); };

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          <DateRangePicker value={range} onChange={setRange} label="Dates" minWidth={220} />
          <SearchableSelect label="Account" value={accountId} onChange={setAccountId} options={accountOptions} placeholder="All accounts" minWidth={240} />
          <SearchableSelect label="Source" value={sourceType} onChange={setSourceType} options={JOURNAL_SOURCE_OPTIONS} placeholder="All sources" minWidth={190} />
          <SearchableSelect
            label="Status"
            value={status}
            onChange={v => setStatus(v as JournalStatus | '')}
            options={STATUS_OPTIONS}
            placeholder="All statuses"
            minWidth={170}
          />
          <TextField
            size="small"
            placeholder="Search voucher or description…"
            value={q}
            onChange={e => setQ(e.target.value)}
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" color="action" /></InputAdornment> }}
          />
          {filtered && <Button size="small" onClick={reset}>Reset</Button>}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
        {loading && items.length > 0 && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2 }} />}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.02) }}>
                <TableCell sx={{ width: 130 }}>Date</TableCell>
                <TableCell sx={{ width: 170 }}>Voucher</TableCell>
                <TableCell>Description</TableCell>
                <TableCell sx={{ width: 160 }}>Source</TableCell>
                <TableCell sx={{ width: 150 }} align="right">Amount</TableCell>
                <TableCell sx={{ width: 110 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && items.length === 0 ? <TableRowSkeletons columns={6} /> : items.map(e => (
                <TableRow key={e._id} hover onClick={() => onOpen(e._id)} sx={{ cursor: 'pointer' }}>
                  <TableCell><Typography variant="body2">{formatDate(e.entryDate)}</Typography></TableCell>
                  <TableCell><Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace' }}>{e.entryNo}</Typography></TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 420 }}>{e.memo}</Typography>
                    {e.createdByName && <Typography variant="caption" color="text.secondary">{e.createdByName}</Typography>}
                  </TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary">{JOURNAL_SOURCE_LABEL[e.sourceType] || e.sourceType}</Typography></TableCell>
                  <TableCell align="right"><Amount paisa={e.totalPaisa} fontWeight={600} /></TableCell>
                  <TableCell><JournalStatusChip entry={e} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {!loading && items.length === 0 && (
          <Box sx={{ p: 3 }}>
            <EmptyState
              icon={<MenuBookOutlined />}
              title={error ? 'The journal could not load' : filtered ? 'No entries match' : 'No journal entries yet'}
              description={error || (filtered ? 'Clear a filter to see more.' : 'Every posting appears here as a numbered voucher.')}
              dense
            />
          </Box>
        )}

        <TablePagination
          component="div"
          count={data?.pagination.totalItems ?? 0}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={limit}
          onRowsPerPageChange={e => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

export default JournalEntriesTable;
